import { blockFacetten, type Facette } from './gelaende-block';

/**
 * Schraegbild zu einer Hangneigungsstufe: derselbe Gelaendeblock wie bei der
 * Woelbung, nur unterschiedlich stark gekippt. Nebeneinander sieht man damit
 * auf einen Blick, was „Flachhang“ von „Lehnhang“ und „Steilhang“ trennt.
 *
 * ⚠ Die Darstellung ist UEBERHOEHT, und zwar mit Absicht: In Wirklichkeit sind
 * ein Hang von 1 Grad und einer von 3 Grad beide praktisch flach und in einem
 * 50 Pixel grossen Bild nicht zu unterscheiden. Gezeigt wird deshalb nicht der
 * echte Winkel, sondern eine gestaffelte Reihe, die die REIHENFOLGE der Stufen
 * sichtbar macht. Wer den Winkel braucht, liest ihn in der Spalte daneben.
 */

/** Groesster Ausschlag im Bild (beim steilsten Hang). */
const STAERKSTE_NEIGUNG = 0.85;

/**
 * Kippung im Bild zu einem Hangwinkel. Die Wurzel spreizt die flachen Stufen
 * auf: Ohne sie waeren die ersten vier Stufen im Bild nicht zu unterscheiden.
 */
export function bildNeigung(grad: number): number {
	const anteil = Math.min(Math.max(grad, 0), 90) / 90;
	return STAERKSTE_NEIGUNG * Math.sqrt(anteil);
}

/**
 * Hoehe des geneigten Blocks. Er kippt um seine Mitte: hinten (bergauf) hoch,
 * vorn (Hangfuss) tief.
 */
export function neigungHoehe(grad: number, laengs: number): number {
	return -bildNeigung(grad) * laengs;
}

/** Die Facetten des Schraegbildes zu einem Hangwinkel. */
export function neigungFacetten(grad: number): Facette[] {
	return blockFacetten(laengs => neigungHoehe(grad, laengs));
}

/**
 * Die Winkelbereiche der Stufen.
 *
 * ⚠ Zweitschrift: Die Bereiche stehen auch in `assets/json/Neigungsstufe.json`
 * (dort als `minGrad`/`maxGrad`). `neigung-raumbild.spec.ts` gleicht beide ab,
 * damit das Bild nicht eines Tages einen anderen Hang zeigt als die Liste sagt.
 */
export const NEIGUNGSSTUFEN: { id: string; minGrad: number; maxGrad: number }[] = [
	{ id: 'Ebene', minGrad: 0, maxGrad: 1 },
	{ id: 'schwächer geneigter Flachhang', minGrad: 1, maxGrad: 3 },
	{ id: 'stärker geneigter Flachhang', minGrad: 3, maxGrad: 7 },
	{ id: 'schwächer geneigter Lehnhang', minGrad: 7, maxGrad: 15 },
	{ id: 'stärker geneigter Lehnhang', minGrad: 15, maxGrad: 25 },
	{ id: 'schwächer geneigter Steilhang', minGrad: 25, maxGrad: 60 },
	{ id: 'stärker geneigter Steilhang', minGrad: 60, maxGrad: 90 },
];

/**
 * Der Winkel, mit dem eine Stufe gezeigt wird: die Mitte ihres Bereichs.
 * Beim steilsten Hang gibt es in der Sache keine Obergrenze; die Liste fuehrt
 * 90 Grad als Ende, gezeigt wird die Mitte davon.
 */
export function stufenWinkel(stufe: string): number | null {
	const eintrag = NEIGUNGSSTUFEN.find(s => s.id === stufe);
	return eintrag ? (eintrag.minGrad + eintrag.maxGrad) / 2 : null;
}

/** Die Facetten des Schraegbildes zu einer Neigungsstufe. */
export function neigungFacettenFuerStufe(stufe: string): Facette[] {
	const winkel = stufenWinkel(stufe);
	return winkel === null ? [] : neigungFacetten(winkel);
}
